import { IRichContentEditorState, ITemplate } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { getDefaultDateStringValue, getDefaultTimeStringValue } from '../../../../../models/UiUtils';
import { Button } from '../../../../components-v2/Button';
import {
	EmailMessageTemplateSaveButton,
	IEmailMessageTemplateSaveButtonProps,
} from '../EmailMessageTemplateSaveButton';
import { styleSheet } from './styles';

interface IProps extends IEmailMessageTemplateSaveButtonProps {
	canDelete?: boolean;
	canImpersonateOnTemplateSave?: boolean;
	className?: string;
	disableEditing?: boolean;
	isEditing?: boolean;
	isQueuedCampaign?: boolean;
	editorState?: IRichContentEditorState;
	onDeleteClicked?(e: React.MouseEvent<HTMLElement, MouseEvent>): void;
	onEditClicked?(e: React.MouseEvent<HTMLElement, MouseEvent>): void;
	onPreviewClicked?(e: React.MouseEvent<HTMLElement, MouseEvent>): void;
	styles?: StyleDeclarationValue[];
}

export const EmailMessageTemplateButtons: React.FC<React.PropsWithChildren<IProps>> = props => {
	const {
		canDelete,
		canImpersonateOnTemplateSave,
		children,
		disableEditing,
		editorState,
		className,
		isEditing,
		isQueuedCampaign,
		onDeleteClicked,
		onEditClicked,
		onPreviewClicked,
		onTemplateSaved,
		styles,
		...restProps
	} = props;
	const [saveText, setSaveText] = React.useState('');

	const updateSaveText = React.useCallback(
		(template: ITemplate) => {
			if (template?.name) {
				const date = new Date();
				setSaveText(`"${template.name}" saved ${getDefaultDateStringValue(date)} ${getDefaultTimeStringValue(date)}`);
			}
			onTemplateSaved?.(template);
		},
		[onTemplateSaved]
	);

	const text = isEditing ? (isQueuedCampaign ? 'Save' : 'Preview') : 'Edit';

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} email-message-template-buttons ${className || ''}`}
		>
			<div className={css(styleSheet.saveText)}>{saveText}</div>
			{children}
			{!(text === 'Edit' && disableEditing) && (
				<div className={css(text === 'Edit' && disableEditing && styleSheet.disabledEdit)}>
					<Button
						variant='secondary'
						size='sm'
						disabled={text === 'Edit' && disableEditing}
						onClick={isEditing ? onPreviewClicked : onEditClicked}
					>
						{text}
					</Button>
				</div>
			)}
			{!isQueuedCampaign && (
				<EmailMessageTemplateSaveButton
					editorState={editorState}
					canImpersonateOnTemplateSave={canImpersonateOnTemplateSave}
					onTemplateSaved={updateSaveText}
					{...restProps}
				/>
			)}
			<Button variant='destructive-secondary' size='sm' disabled={!canDelete} onClick={onDeleteClicked}>
				Delete
			</Button>
		</div>
	);
};
